/**
 * Day Store — tracks the current date string for reactive view invalidation.
 *
 * When the day changes (midnight crossover), `update()` is called by the
 * `dayChangeDetection` background task. Views that depend on the current day
 * subscribe via `useDayStore(s => s.today)` and add it to their live-query
 * deps array so queries re-fire with the new date boundary.
 *
 * Zustand's getState() allows non-React code (background tasks) to update.
 */

import { create } from "zustand"
import dayjs from "dayjs"

interface DayState {
  /** Current day as "YYYY-MM-DD" */
  today: string

  /**
   * Check if the day has changed and update the store if so.
   * @returns `true` if the day actually changed.
   */
  update: () => boolean
}

export const useDayStore = create<DayState>((set, get) => ({
  today: dayjs().format("YYYY-MM-DD"),
  update: () => {
    const newDay = dayjs().format("YYYY-MM-DD")
    if (newDay !== get().today) {
      console.info(`[dayStore] Day changed: ${get().today} → ${newDay}`)
      set({ today: newDay })
      return true
    }
    return false
  },
}))
